import { useRef, useState } from 'react'
import type { Scan } from '../types/scan'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import { generateCaseFilePDF } from '../utils/pdfGenerator'
import { trackShare } from '../utils/mixpanelTracking'
import {
  trackResultDownloaded,
  trackResultShared,
  type ShareMethod,
} from '../utils/analytics'
import { trackEvent } from '../utils/visitorTracking'
import { SOCIAL_CAPTIONS, SHARE_IMAGE_URL } from '../data/socialCaptions'
import {
  getMessageCount,
  getReportRecommendations,
  resolveRiskLevel,
} from '../utils/scanReport'

export type AspectRatio = 'square' | 'story'
export type SharePlatform =
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'twitter'
  | 'youtube'
  | 'whatsapp'

/** Image, PDF, and social sharing for one scan's report. */
export function useReportSharing(scan: Scan | null, hasFullAccess = false) {
  const { user } = useAuth()
  const { showToast } = useToast()
  const [shareAspectRatio, setShareAspectRatio] = useState<AspectRatio>('story')
  const [shareCardTheme, setShareCardTheme] = useState<'dark' | 'light'>('dark')
  const [isGeneratingImage, setIsGeneratingImage] = useState(false)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const squareCaptureRef = useRef<HTMLDivElement>(null)
  const storyCaptureRef = useRef<HTMLDivElement>(null)
  const userPlan = user?.user_metadata?.subscriptionPlan || 'free'

  const handleDownloadImage = async () => {
    if (!scan) return
    setIsGeneratingImage(true)
    try {
      const element =
        shareAspectRatio === 'square'
          ? squareCaptureRef.current
          : storyCaptureRef.current
      if (!element) throw new Error('Capture element not found')
      const { toPng } = await import('html-to-image')
      const dataUrl = await toPng(element, { quality: 1, pixelRatio: 2 })
      const link = document.createElement('a')
      link.download = `toxic-report-${scan.id}-${shareAspectRatio}.png`
      link.href = dataUrl
      link.click()
      showToast('Image downloaded successfully!', 'success')
      trackResultDownloaded('png')
      await trackShare('Image_Download', scan.score || 0, userPlan)
    } catch (error) {
      console.error('Error downloading image:', error)
      showToast('Failed to generate image. Please try again.', 'error')
    } finally {
      setIsGeneratingImage(false)
    }
  }

  /** The complete PDF is a paid deliverable — gated on is_pro || is_unlocked. */
  const handleDownloadPDF = async () => {
    if (!scan || !hasFullAccess) return
    setIsGeneratingPDF(true)
    try {
      const analysisData = scan.analysis
      await generateCaseFilePDF({
        scanId: scan.id,
        score: scan.score || 0,
        riskLevel: resolveRiskLevel(scan),
        messageCount: getMessageCount(scan),
        summary: analysisData?.summary,
        findings: analysisData?.findings || [],
        recommendations: getReportRecommendations(scan),
        createdAt: scan.created_at,
        patterns: scan.patterns || [],
        analysis: analysisData,
      })
      showToast('PDF report downloaded.', 'success')
      trackResultDownloaded('pdf')
      await trackShare('PDF_Download', scan.score || 0, userPlan)
    } catch (error) {
      console.error('Error generating PDF:', error)
      showToast('We couldn’t generate the PDF. Please try again.', 'error')
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const handleSocialShare = async (platform: SharePlatform) => {
    if (!scan) return
    const shareUrl = `${window.location.origin}/results?id=${scan.id}&ref=share&score=${scan.score || 0}`
    const caption =
      platform === 'instagram'
        ? SOCIAL_CAPTIONS.instagram_feed
        : platform === 'whatsapp'
          ? SOCIAL_CAPTIONS.twitter
          : SOCIAL_CAPTIONS[platform]
    const platformName = platform.charAt(0).toUpperCase() + platform.slice(1)
    try {
      if (navigator.share) {
        try {
          const response = await fetch(SHARE_IMAGE_URL)
          const blob = await response.blob()
          const file = new File([blob], 'toxic-detector-result.png', {
            type: blob.type,
          })
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: 'ToxicDetector Analysis',
              text: caption,
              url: shareUrl,
              files: [file],
            })
            trackResultShared('native_share', 'scan_result')
            void trackEvent('result_shared', {
              scan_id: scan.id,
              platform,
              method: 'native_share',
              score: scan.score ?? null,
            })
            await trackShare(platformName, scan.score || 0, userPlan)
            return
          }
        } catch (e) {
          console.log('Native file share failed, falling back', e)
        }
      }
      if (['instagram', 'tiktok', 'youtube'].includes(platform)) {
        await copyText(`${caption}\n\n${shareUrl}`)
        showToast(
          `Caption and link copied. Open ${platformName} and paste it.`,
          'success',
        )
      } else if (platform === 'whatsapp') {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(caption + ' ' + shareUrl)}`,
          '_blank',
          'width=600,height=400',
        )
      } else if (platform === 'facebook') {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`,
          '_blank',
          'width=600,height=400',
        )
      } else if (platform === 'twitter') {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'width=600,height=400',
        )
      }
      trackResultShared(platform as ShareMethod, 'scan_result')
      void trackEvent('result_shared', {
        scan_id: scan.id,
        platform,
        method: ['instagram', 'tiktok', 'youtube'].includes(platform)
          ? 'copy_link'
          : 'web_intent',
        score: scan.score ?? null,
      })
      await trackShare(platformName, scan.score || 0, userPlan)
    } catch (err) {
      console.error('Share error:', err)
      showToast('Unable to share right now. Please try again.', 'error')
    }
  }

  return {
    shareAspectRatio,
    setShareAspectRatio,
    shareCardTheme,
    setShareCardTheme,
    isGeneratingImage,
    isGeneratingPDF,
    squareCaptureRef,
    storyCaptureRef,
    handleDownloadImage,
    handleDownloadPDF,
    handleSocialShare,
  }
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textArea = document.createElement('textarea')
  textArea.value = text
  document.body.appendChild(textArea)
  textArea.select()
  document.execCommand('copy')
  document.body.removeChild(textArea)
}
